import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Typography,
  Box,
  FormControl,
  TextField,
  Button,
  Divider,
  Stack,
  FormHelperText,
  Skeleton,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { alpha, styled } from '@mui/material/styles';
import GoogleMapWithDirections from 'src/@carketa/sales-packet/components/GoogleMapWithDirections';
import { useJsApiLoader } from '@react-google-maps/api';
import { CarketaDisclosure } from 'src/@carketa/sales-packet/components/CarketaDisclosure';

import { ReactNode, useState } from 'react';
import { zipCodeToPlaceName } from 'src/utils/google/maps';
import { useAnalytics } from 'use-analytics';

const LinkStyled = styled('a')(({ theme }) => ({
  fontSize: 12,
  textDecoration: 'none',
  cursor: 'pointer',
  color: theme.palette.primary.main,
  '&:hover': {
    background: alpha(theme.palette.common.white, 0.25),
  },
}));

const schema = z.object({
  fromZip: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'ZIP code must be 5 digits'),
  toZip: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'ZIP code must be 5 digits'),
});

interface ShippingQuoteFields {
  fromZip: string;
  toZip: string;
  units: number;
  inop: boolean;
  is_enclosed: boolean;
}

type ShippingQuoteProps = {
  rate?: number | undefined;
  miles?: number | undefined;
  eta_min?: number | undefined;
  eta_max?: number | undefined;
  inop_fee?: number | undefined;
  rebate_rate?: number | undefined;
  error?: string | undefined;
  units: ShippingQuoteFields['units'];
};

interface TransportationProps {
  pdfMode?: boolean;
}

const Transportation = ({ pdfMode }: TransportationProps) => {
  const { track } = useAnalytics();

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ShippingQuoteFields>({
    defaultValues: {
      fromZip: '',
      toZip: '',
    },
    resolver: zodResolver(schema),
  });

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyDjx0QsOIj6kV1G-UkHSzZBtiRwaDQ9TrM', //this needs to be in the .env should call process.env.GOOGLE_MAP_API_KEY
    libraries: ['geometry'],
  });

  const [isLoading, setIsLoading] = useState(false);
  const [quote, setQuote] = useState<ShippingQuoteProps | null>(null);

  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');

  const rate = quote?.rate || 0;
  const perVehicleRate = (rate + (quote?.inop_fee || 0))
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const perVehicleRateFormatted = perVehicleRate
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const totalRate = ((rate || 0) + (quote?.inop_fee || 0)) * 1;

  const totalRateFormatted = totalRate
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const getQuote = async (data: ShippingQuoteFields) => {
    setIsLoading(true);

    const url = new URL('/api/shipping/quote', window.location.origin);
    url.searchParams.append('pickup', data.fromZip);
    url.searchParams.append('destination', data.toZip);
    url.searchParams.append('units', '1');
    url.searchParams.append('inop', '0');
    url.searchParams.append('is_enclosed', '0');

    track(
      'transportation-get-quote-submit',
      {
        url: url.toString(),
      },
      {
        component: 'sales-packet',
      }
    );

    const response = await fetch(url.toString());

    const results = await response.json();

    track(
      'transportation-get-quote-results',
      {
        results,
      },
      {
        component: 'sales-packet',
      }
    );

    if (response.ok) {
      if (isLoaded) {
        const originData = await zipCodeToPlaceName(results.pickup);
        const destData = await zipCodeToPlaceName(results.destination);

        setQuote(results);
        setIsLoading(false);
        setOrigin(originData);
        setDestination(destData);
      }

      return results;
    } else {
      setIsLoading(false);
      console.error('An error occurred while fetching the shipping quote');
    }
    setIsLoading(false);
    return null;
  };

  return (
    <>
      <Typography variant="h6" sx={{ mb: 5, color: '#000', mt: 5 }}>
        Transportation
      </Typography>
      <div className="p-2 w-full outline outline-1 outline-[#bdbdbd] rounded">
        <div className="p-3">
          <Typography variant="h6" sx={{ color: '#000' }}>
            Get a quote
          </Typography>
          <Typography variant="caption">Enter shipping information</Typography>
          <form
            onSubmit={handleSubmit((data) => {
              getQuote(data);
            })}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 6,
                gap: 4,
                mt: 4,
              }}
            >
              <FormControl fullWidth>
                <Controller
                  name="fromZip"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      value={value}
                      //   required
                      label="Origin Zip Code"
                      onChange={onChange}
                      // placeholder="John"
                      error={Boolean(errors.fromZip)}
                    />
                  )}
                />
                {errors?.fromZip && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {(errors.fromZip.message as ReactNode) || ''}
                  </FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth>
                <Controller
                  name="toZip"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      fullWidth
                      value={value}
                      label="Destination Zip Code"
                      onChange={onChange}
                      error={Boolean(errors.toZip)}
                    />
                  )}
                />
                {errors?.toZip && (
                  <FormHelperText sx={{ color: 'error.main' }}>
                    {(errors.toZip.message as ReactNode) || ''}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 6 }}>
              <Button
                size="large"
                type="submit"
                variant="contained"
                sx={{ mr: 3 }}
              >
                Get Quote
              </Button>
              <Button
                size="large"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setQuote(null);
                  reset();
                }}
              >
                Clear
              </Button>
            </Box>
          </form>
          <Divider sx={{ mb: 6 }} />
          <Stack>
            <Typography variant="caption">
              Having Transportation Issues?{' '}
              <LinkStyled href={`mailto:carketach@acertusdelivers.com`}>
                carketach@acertusdelivers.com
              </LinkStyled>{' '}
              <LinkStyled href={`tel:(855) 431-6570`}>
                (855) 431-6570
              </LinkStyled>
            </Typography>
          </Stack>
        </div>
      </div>

      {isLoading ? (
        <>
          <Box sx={{ mt: 5 }}>
            <Skeleton variant="rectangular" width={'100%'} height={100} />
          </Box>
        </>
      ) : (
        quote && (
          <div className="p-2 w-full outline outline-1 outline-[#bdbdbd] rounded mt-4">
            <div className="p-3">
              <Typography variant="h6" sx={{ color: '#000' }}>
                Quote Details
              </Typography>
              <div className="flex mt-6">
                <strong className="text-12 mb-1 mr-2">Rate:</strong>
                <Typography variant="caption" ml={1}>
                  {quote?.rate
                    ? `$${perVehicleRateFormatted}

          per unit = $${totalRateFormatted} total`
                    : ''}
                </Typography>
              </div>
              <div className="flex">
                <strong className="text-12 mb-1 mr-2">Miles:</strong>
                <Typography variant="caption" ml={1}>
                  {quote?.miles
                    ?.toString()
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Typography>
              </div>
              <div className="flex">
                <strong className="text-12 mb-1 mr-2">ETA:</strong>
                <Typography variant="caption" ml={1}>
                  {quote?.eta_min && quote.eta_max
                    ? `${quote?.eta_min} - ${quote?.eta_max} days`
                    : ''}
                </Typography>
              </div>

              <div className="w-full mt-2">
                {!isLoaded ? (
                  <p>Loading...</p>
                ) : (
                  <GoogleMapWithDirections
                    origin={origin}
                    destination={destination}
                  />
                )}
              </div>
            </div>
          </div>
        )
      )}
      {pdfMode && <CarketaDisclosure />}
    </>
  );
};

export default Transportation;
